interface FuelGaugeProps {
  percentage: number | null;
  label: string;
  color?: string;
  dueDate?: string;
  hasError?: boolean;
  errorMessage?: string;
  assignmentId?: string;
  onClick?: () => void;
}

function formatDueDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();

  // Format day of week (e.g., "Mon", "Tues", "Weds")
  const dayNames = ['Sun', 'Mon', 'Tues', 'Weds', 'Thurs', 'Fri', 'Sat'];
  const dayOfWeek = dayNames[date.getDay()];

  // Format time (e.g., "10pm", "2:30pm")
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  const timeStr = minutes === 0 ? `${hours}${ampm}` : `${hours}:${minutes.toString().padStart(2, '0')}${ampm}`;

  // Helper function to add ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
  const getOrdinalSuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Check if it's today, tomorrow, or yesterday
  const isToday = date.toDateString() === now.toDateString();

  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  // Check if date is in the past (but not today or yesterday)
  const isPast = date < now && !isToday && !isYesterday;

  // Check if date is more than 7 days in the future
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  const isMoreThan7Days = diffDays > 7;

  if (isToday) {
    return `Today ${timeStr}`;
  } else if (isTomorrow) {
    return `Tomorrow ${timeStr}`;
  } else if (isYesterday) {
    return `Yesterday ${timeStr}`;
  } else if (isPast) {
    return `last ${dayOfWeek} ${timeStr}`;
  } else if (isMoreThan7Days) {
    const dayOfMonth = date.getDate();
    const ordinalSuffix = getOrdinalSuffix(dayOfMonth);
    return `${dayOfWeek} ${dayOfMonth}${ordinalSuffix} ${timeStr}`;
  } else {
    return `${dayOfWeek} ${timeStr}`;
  }
}

export function FuelGauge({ percentage, label, color = 'blue', dueDate, hasError, errorMessage, assignmentId, onClick }: FuelGaugeProps) {
  const size = 120;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Calculate the stroke offset based on percentage
  const offset = percentage !== null
    ? circumference - (percentage / 100) * circumference
    : circumference;

  const isClickable = onClick && assignmentId && percentage !== null;

  // Color classes
  const colorClasses: Record<string, { stroke: string; text: string; badge: string }> = {
    blue: {
      stroke: 'stroke-blue-500',
      text: 'text-blue-600',
      badge: 'bg-blue-100 text-blue-800',
    },
    green: {
      stroke: 'stroke-green-500',
      text: 'text-green-600',
      badge: 'bg-green-100 text-green-800',
    },
    purple: {
      stroke: 'stroke-purple-500',
      text: 'text-purple-600',
      badge: 'bg-purple-100 text-purple-800',
    },
    orange: {
      stroke: 'stroke-orange-500',
      text: 'text-orange-600',
      badge: 'bg-orange-100 text-orange-800',
    },
  };

  const selectedColors = (colorClasses[color] || colorClasses.blue)!;
  const isComplete = percentage === 100;

  return (
    <div
      className={`flex flex-col items-center scale-75 sm:scale-100 origin-top ${
        isClickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
      }`}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      title={isClickable ? 'Click to view progress history' : undefined}
    >
      <div className="relative" style={{ width: size, height: size }}>
        {/* Background circle */}
        <svg
          className="transform -rotate-90"
          width={size}
          height={size}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="none"
            className="text-gray-200"
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className={`${selectedColors.stroke} transition-all duration-500 ease-in-out`}
          />
        </svg>

        {/* Center text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            {percentage !== null ? (
              isComplete ? (
                <svg className={`w-12 h-12 ${selectedColors.text}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <div className={`text-2xl font-bold ${selectedColors.text}`}>
                  {percentage}%
                </div>
              )
            ) : (
              <div className="text-sm font-medium text-gray-400 px-2">None set</div>
            )}
          </div>
        </div>
      </div>

      {/* Label */}
      <div className="mt-2 text-sm font-medium text-gray-700">{label}</div>

      {/* Error or Due date */}
      {hasError ? (
        <div className="mt-1 text-xs text-red-600 flex items-center justify-center gap-1" title={errorMessage}>
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>Parse error</span>
        </div>
      ) : dueDate && (
        <div className="mt-1 text-xs text-gray-500 text-center">
          Due {formatDueDate(dueDate)}
        </div>
      )}
    </div>
  );
}
